import React, { useEffect, useMemo, useState } from 'react'
import { FaReply, FaRegTrashAlt } from 'react-icons/fa'
import { FiRefreshCw, FiSearch } from 'react-icons/fi'
import AdmiSubHeader from '../../components/subheader/admi-subheader.components'
import CustomEmailDialog from '../../components/forms/custom-email-dialog.components'
import ReplyDialog from '../../components/forms/reply-dialog.components'
import { useOrder } from '../../hooks/useAuth'
import { infoToast } from '../../utils/firebase/firebase.utils'
import { AdmiMenu } from './admi-menu.components'
import './admi.styles.scss'
import './inquiry.styles.scss'

const PAGE_SIZE = 8

const formatInquiryDate = (value) => {
  if (!value) return 'Unknown date'
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown date'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const InquiryView = () => {
  const { inquiries, getInquiry, updateInquiry } = useOrder()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [refreshing, setRefreshing] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  useEffect(() => {
    if (typeof getInquiry === 'function') getInquiry()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPage(1)
  }, [search, filter])

  const refreshInquiries = async () => {
    if (typeof getInquiry !== 'function' || refreshing) return
    setSearch('')
    setFilter('all')
    setPage(1)
    setRefreshing(true)
    try {
      await getInquiry()
    } finally {
      setRefreshing(false)
    }
  }

  const filtered = useMemo(() => {
    const list = Array.isArray(inquiries) ? inquiries.filter((item) => item?.del !== 'yes') : []
    const q = search.trim().toLowerCase()

    return list.filter((item) => {
      const waiting = item.status !== 'yes'
      if (filter === 'waiting' && !waiting) return false
      if (filter === 'replied' && waiting) return false

      if (!q) return true
      const haystack = `${item.name || ''} ${item.email || ''} ${item.message || ''}`.toLowerCase()
      return haystack.includes(q)
    })
  }, [inquiries, search, filter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const counts = useMemo(() => {
    const list = Array.isArray(inquiries) ? inquiries.filter((item) => item?.del !== 'yes') : []
    return {
      all: list.length,
      waiting: list.filter((item) => item.status !== 'yes').length,
      replied: list.filter((item) => item.status === 'yes').length,
    }
  }, [inquiries])

  const deleteInquiry = async (item) => {
    if (!item?.id) {
      return infoToast('This inquiry is missing an id and cannot be deleted.')
    }

    const ok = window.confirm(`Delete inquiry from ${item.name || item.email || 'this customer'}?`)
    if (!ok) return

    setDeletingId(item.id)
    try {
      await updateInquiry(
        {
          ...item,
          del: 'yes',
          updated_at: new Date(),
        },
        'Inquiry deleted.'
      )
    } catch (error) {
      console.error('Delete inquiry failed:', error)
      infoToast('Could not delete inquiry. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="admin-shell">
      <AdmiSubHeader h2="Inquiries" h6="Read messages and reply to customers" />
      <AdmiMenu />

      <div className="admin-shell__body inquiry-v2">
        <div className="admin-toolbar">
          <div className="admin-toolbar__search">
            <FiSearch size={15} aria-hidden="true" />
            <label className="admin-toolbar__search-field">
              <span className="sr-only">Search inquiries</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, email, or message"
              />
            </label>
            <button
              type="button"
              className={`admin-toolbar__refresh${refreshing ? ' is-spinning' : ''}`}
              onClick={refreshInquiries}
              disabled={refreshing}
              aria-label="Clear filters and refresh inquiries"
              title="Clear filters and refresh"
            >
              <FiRefreshCw size={15} aria-hidden="true" />
            </button>
          </div>

          <div className="admin-toolbar__actions">
            <div className="admin-toolbar__filters" role="group" aria-label="Filter inquiries">
              {[
                { id: 'all', label: `All (${counts.all})` },
                { id: 'waiting', label: `Waiting (${counts.waiting})` },
                { id: 'replied', label: `Replied (${counts.replied})` },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={filter === item.id ? 'is-active' : ''}
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <CustomEmailDialog />
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="inquiry-v2__empty">
            {counts.all === 0
              ? 'No inquiries yet. New Contact form messages will show up here.'
              : 'No inquiries match this search or filter.'}
          </p>
        ) : (
          <>
            <p className="inquiry-v2__page-meta">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}
              {'–'}
              {Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
            </p>

            <ul className="inquiry-v2__list">
              {pageItems.map((item) => {
                const waiting = item.status !== 'yes'
                return (
                  <li key={item.id || `${item.email}-${item.created_at}`} className="inquiry-v2__card">
                    <div className="inquiry-v2__card-top">
                      <div>
                        <h3>{item.name || 'Anonymous'}</h3>
                        <a href={`mailto:${item.email}`}>{item.email}</a>
                      </div>
                      <div className={`inquiry-v2__badge${waiting ? ' is-waiting' : ' is-replied'}`}>
                        {waiting ? 'Waiting' : 'Replied'}
                      </div>
                    </div>

                    <p className="inquiry-v2__message">{item.message}</p>
                    <p className="inquiry-v2__meta">{formatInquiryDate(item.created_at)}</p>

                    <div className="inquiry-v2__actions">
                      {waiting ? <ReplyDialog inquiry={item} /> : null}

                      {!waiting ? (
                        <div className="inquiry-v2__reply">
                          <p className="inquiry-v2__reply-label">
                            <FaReply size={12} aria-hidden="true" /> Your reply
                          </p>
                          <p>{item.inq_response || 'Reply sent.'}</p>
                        </div>
                      ) : null}

                      <button
                        type="button"
                        className="inquiry-v2__delete"
                        onClick={() => deleteInquiry(item)}
                        disabled={deletingId === item.id}
                        aria-label={`Delete inquiry from ${item.name || item.email || 'customer'}`}
                      >
                        <FaRegTrashAlt size={13} aria-hidden="true" />
                        {deletingId === item.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>

            {totalPages > 1 ? (
              <div className="inquiry-v2__pager">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </button>
              </div>
            ) : null}
          </>
        )}

        <p className="inquiry-v2__mail-note">
          Sending a reply updates Firestore. If Cloud Functions mail is deployed, the customer also
          gets an email. Delete hides the inquiry from this list (soft delete).
        </p>
      </div>
    </div>
  )
}

export default InquiryView
